import { useMemo } from 'react';

import { usePlanFeature } from '~/hooks/usePlanFeature';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';

type ShareFeatureFlags = {
  commentsEnabled: boolean;
  canManageCommentsEnabled: boolean;
  customFieldsEnabled: boolean;
  /** Layout authoring (page width, corner rounding) — any paid plan. */
  designSettingsEnabled: boolean;
  /** Brand-element authoring (custom logo, custom colors, light/dark page theme) — Business+. */
  brandCustomizationEnabled: boolean;
  /** Custom headline font authoring — Enterprise only. */
  customTypographyEnabled: boolean;
  metadataEnabled: boolean;
  requireAccountEnabled: boolean;
  passcodeEnabled: boolean;
  expirationDateEnabled: boolean;
  removeAirLogoEnabled: boolean;
  contentBlocksEnabled: boolean;
  subPagesEnabled: boolean;
};

export const useShareSettings = (): ShareFeatureFlags => {
  const { data: subscription } = useSubscriptionContext();
  const canUsePasscodeProtectedLinks = usePlanFeature('passcodeProtectedLinks');

  return useMemo<ShareFeatureFlags>(() => {
    const isPaid = !!subscription?.isOnPaidPlan;

    return {
      commentsEnabled: isPaid,
      canManageCommentsEnabled: isPaid,
      customFieldsEnabled: isPaid,
      designSettingsEnabled: isPaid,
      brandCustomizationEnabled: !!subscription?.isOnBusinessPlan,
      customTypographyEnabled: !!subscription?.isOnEnterprisePlan,
      metadataEnabled: isPaid,
      requireAccountEnabled: isPaid,
      passcodeEnabled: canUsePasscodeProtectedLinks,
      expirationDateEnabled: isPaid,
      removeAirLogoEnabled: !!subscription?.isOnEnterprisePlan,
      // FOUND-625: AUTHORING content blocks and the nav section is Enterprise-only. `subPagesEnabled`
      // covers the WHOLE nav section — sub-pages AND external links — because product treats nav
      // authoring as one capability (answered 2026-08-11), not two separately-sold ones.
      //
      // Both flags gate CREATION alone — a workspace that downgrades keeps editing, reordering,
      // hiding and deleting the blocks, pages and links it already authored, and the public viewer
      // renders them all regardless. Gating the render would break links an Enterprise workspace has
      // already published.
      contentBlocksEnabled: !!subscription?.isOnEnterprisePlan,
      subPagesEnabled: !!subscription?.isOnEnterprisePlan,
    };
  }, [subscription, canUsePasscodeProtectedLinks]);
};
